import React, {createContext, useContext, useState} from "react";
import { useEventContext } from "./EventContext";
import {
    updateEventModuleActivity,
    updateEventModuleBranding,
    updateEventModuleComment,
    updateEventModuleEpisode,
    updateEventModuleInformation,
    updateEventModuleMain,
    updateEventModulePage,
    updateEventModuleQuestion,
    updateEventModuleQuiz,
    updateEventModuleShop,
} from "../../api/cp";
import { createGetterFromSetState } from "../../platform/src/helpers/createGetterFromSetState";

export const ModuleSettingsContext = createContext(null);

export const ModuleSettingsProvider = ({ children }) => {
    const { event, setEvent } = useEventContext();
    const [moduleBranding, setModuleBranding] = useState(event.moduleBranding || {});
    const [moduleEpisode, setModuleEpisode] = useState(event.moduleEpisode || {});
    const [moduleComment, setModuleComment] = useState(event.moduleComment || {});
    const [moduleQuestion, setModuleQuestion] = useState(event.moduleQuestion || {});
    const [moduleActivity, setModuleActivity] = useState(event.moduleActivity || {});
    const [moduleInformation, setModuleInformation] = useState(event.moduleInformation || {});
    const [modulePage, setModulePage] = useState(event.modulePage || {});
    const [moduleMain, setModuleMain] = useState(event.moduleMain || {});
    const [moduleQuiz, setModuleQuiz] = useState(event.moduleQuiz || {});
    const [moduleShop, setModuleShop] = useState(event.moduleShop || {});
    const [isSaving, setSaving] = useState(false);
    const getModuleInformationState = createGetterFromSetState(setModuleInformation);

    const onSaveBranding = async (data) => {
        setModuleBranding(moduleBranding => ({ ...moduleBranding, ...data }));
        setSaving(true);
        try {
            const moduleBranding = await updateEventModuleBranding(event.eventKey, data);
            setModuleBranding(moduleBranding || {});
            setEvent(event => ({ ...event, moduleBranding }));
        } catch (e) {
            console.error(e);
        } finally {
            setSaving(false);
        }
    }

    const onSaveEpisode = async (data) => {
        setModuleEpisode(moduleEpisode => ({ ...moduleEpisode, ...data }));
        setSaving(true);
        try {
            const moduleEpisode = await updateEventModuleEpisode(event.eventKey, data);
            setModuleEpisode(moduleEpisode || {});
            setEvent(event => ({ ...event, moduleEpisode }));
        } catch (e) {
            console.error(e);
        } finally {
            setSaving(false);
        }
    }

    const onSaveComment = async (data) => {
        setModuleComment(moduleComment => ({ ...moduleComment, ...data }));
        setSaving(true);
        try {
            const moduleComment = await updateEventModuleComment(event.eventKey, data);
            setModuleComment(moduleComment || {});
            setEvent(event => ({ ...event, moduleComment }));
        } catch (e) {
            console.error(e);
        } finally {
            setSaving(false);
        }
    }

    const onSaveQuestion = async (data) => {
        setModuleQuestion(moduleQuestion => ({ ...moduleQuestion, ...data }));
        setSaving(true);
        try {
            const moduleQuestion = await updateEventModuleQuestion(event.eventKey, data);
            setModuleQuestion(moduleQuestion || {});
            setEvent(event => ({ ...event, moduleQuestion }));
        } catch (e) {
            console.error(e);
        } finally {
            setSaving(false);
        }
    }

    const onSaveActivity = async (data) => {
        setModuleActivity(moduleActivity => ({ ...moduleActivity, ...data }));
        setSaving(true);
        try {
            const moduleActivity = await updateEventModuleActivity(event.eventKey, data);
            setModuleActivity(moduleActivity || {});
            setEvent(event => ({ ...event, moduleActivity }));
        } catch (e) {
            console.error(e);
        } finally {
            setSaving(false);
        }
    }

    const onSaveInformation = async () => {
        const data = await getModuleInformationState();
        setSaving(true);
        try {
            const moduleInformation = await updateEventModuleInformation(event.eventKey, data);
            setModuleInformation(moduleInformation || {});
            setEvent(event => ({ ...event, moduleInformation }));
        } catch (e) {
            console.error(e);
        } finally {
            setSaving(false);
        }
    }

    const onSavePage = async (data) => {
        setModulePage(modulePage => ({ ...modulePage, ...data }));
        setSaving(true);
        try {
            const modulePage = await updateEventModulePage(event.eventKey, data);
            setModulePage(modulePage || {});
            setEvent(event => ({ ...event, modulePage }));
        } catch (e) {
            console.error(e);
        } finally {
            setSaving(false);
        }
    }

    const onSaveMain = async (data) => {
        setModuleMain(moduleMain => ({ ...moduleMain, ...data }));
        setSaving(true);
        try {
            const moduleMain = await updateEventModuleMain(event.eventKey, data);
            setModuleMain(moduleMain || {});
            setEvent(event => ({ ...event, moduleMain }));
        } catch (e) {
            console.error(e);
        } finally {
            setSaving(false);
        }
    }

    const onSaveQuiz = async (data) => {
        setModuleQuiz(moduleQuiz => ({ ...moduleQuiz, ...data }));
        setSaving(true);
        try {
            const moduleQuiz = await updateEventModuleQuiz(event.eventKey, data);
            setModuleQuiz(moduleQuiz || {});
            setEvent(event => ({ ...event, moduleQuiz }));
        } catch (e) {
            console.error(e);
        } finally {
            setSaving(false);
        }
    }

    const onSaveShop = async (data) => {
        setSaving(true);
        try {
            const moduleShop = await updateEventModuleShop(event.eventKey, data);
            setModuleShop(moduleShop || {});
            setEvent(event => ({ ...event, moduleShop }));
        } catch (e) {
            console.error(e);
        } finally {
            setSaving(false);
        }
    }

    const onSavePageFile = async (key, file) => {
        const formData = new FormData();
        formData.append(key, file);
        setSaving(true);
        try {
            const modulePage = await updateEventModulePage(event.eventKey, formData);
            setModulePage(modulePage);
            setEvent(event => ({ ...event, modulePage }));
        } catch (e) {
            console.error(e);
        } finally {
            setSaving(false);
        }
    }

    const setPropertyBranding = (key, value) => {
        setModuleBranding(moduleBranding => ({ ...moduleBranding, [key]: value }));
    }

    const setPropertyComment = (key, value) => {
        setModuleComment(moduleComment => ({ ...moduleComment, [key]: value }));
    }

    const setPropertyQuestion = (key, value) => {
        setModuleQuestion(moduleQuestion => ({ ...moduleQuestion, [key]: value }));
    }

    const setPropertyEpisode = (key, value) => {
        setModuleEpisode(moduleEpisode => ({ ...moduleEpisode, [key]: value }));
    }

    const setPropertyActivity = (key, value) => {
        setModuleActivity(moduleActivity => ({ ...moduleActivity, [key]: value }));
    }

    const setPropertyInformation = (key, value) => {
        setModuleInformation(moduleInformation => ({ ...moduleInformation, [key]: value }));
    }

    const setPropertyPage = (key, value) => {
        setModulePage(modulePage => ({ ...modulePage, [key]: value }));
    }

    const setPropertyMain = (key, value) => {
        setModuleMain(moduleMain => ({ ...moduleMain, [key]: value }));
    }

    const context = {
        moduleBranding,
        moduleEpisode,
        moduleComment,
        moduleQuestion,
        moduleActivity,
        moduleInformation,
        modulePage,
        moduleMain,
        moduleQuiz,
        moduleShop,
        isSaving,
        onSaveBranding,
        onSaveEpisode,
        onSaveComment,
        onSaveQuestion,
        onSaveActivity,
        onSaveInformation,
        onSavePage,
        onSavePageFile,
        onSaveMain,
        onSaveQuiz,
        onSaveShop,
        setPropertyBranding,
        setPropertyComment,
        setPropertyQuestion,
        setPropertyEpisode,
        setPropertyActivity,
        setPropertyInformation,
        setPropertyPage,
        setPropertyMain,
    }

    return (
        <ModuleSettingsContext.Provider value={context}>
            {children}
        </ModuleSettingsContext.Provider>
    );
}

export const useModuleSettingsContext = () => useContext(ModuleSettingsContext);
